import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { todasAsLinhas } from '@/components/atomic-crm/providers/supabase/todasAsLinhas';
import { agruparInadimplentes, agruparPendentes } from '../blocos/agrupar';
import type { DashboardData, InadimplenteRow, PendingSale } from '../blocos/tipos';
import type { VendasPorMes } from './periodo';

/**
 * A carga de dados do Início.
 *
 * Era o corpo do HomeDashboard; virou hook quando a tela virou bento — o
 * componente ficou só com composição e o dado ganhou casa própria. A lógica de
 * escopo é a mesma de sempre: super admin vê tudo (ou "vê como" alguém),
 * gerente vê a equipe, vendedor vê o seu. Vendas/propostas filtram por
 * sales_person_id; inadimplentes por CPF do vendedor.
 */

const PENDING_STATUSES = ['started', 'pending', 'reconciled'];

const toDigits = (value: string | null | undefined) =>
  typeof value === 'string' ? value.replace(/\D/g, '') : '';

/** DashboardData + o que só o bento mostra. */
export type DadosInicio = DashboardData & {
  /** Produção acumulada dia a dia no mês corrente (regra de vendasMes). */
  producaoDiaria: { dia: number; acumulado: number }[];
  /** Contemplações com data_contemplacao dentro do mês corrente. */
  contemplacoesMesCount: number;
  contemplacoesMesValor: number;
  /**
   * Produção do mês ANTERIOR até o mesmo dia de hoje — é a base honesta do
   * badge "vs. julho": comparar 24 dias de agosto com julho inteiro sempre
   * parece queda.
   */
  vendasMesAnteriorMesmoPeriodo: number;
  /** Contemplações por mês, alinhadas aos 6 buckets de `monthly`. */
  contemplacoesPorMes: number[];
  /**
   * Vendas por mês ('YYYY-MM') sob a MESMA régua de vendasMes — confirmadas,
   * contempladas fora — e sem janela: a consulta já traz o histórico inteiro.
   *
   * É a base dos períodos dos cards de produção (mês, semestre, ano) e da
   * comparação com o período anterior. Os buckets de `monthly` não servem: são
   * 12 meses ROLANTES (não fecham um ano civil), não alcançam o ano passado e
   * contam as contempladas — três divergências que apareceriam como números
   * diferentes para a mesma pergunta na mesma tela.
   */
  vendasPorMes: VendasPorMes;
};

const vazio: DadosInicio = {
  vendasAtivasCount: 0,
  vendasAtivasValor: 0,
  vendasAtivasOwnCount: 0,
  vendasAnoValor: 0,
  vendasAnoCount: 0,
  vendasMesValor: 0,
  vendasMesCount: 0,
  inadCount: 0,
  inadValor: 0,
  inadOwnCount: 0,
  propostasCount: 0,
  propostasOwnCount: 0,
  comissoesMes: 0,
  pendingCount: 0,
  pendingValor: 0,
  monthly: [],
  topInadimplentes: [],
  topPendentes: [],
  producaoDiaria: [],
  contemplacoesMesCount: 0,
  contemplacoesMesValor: 0,
  vendasMesAnteriorMesmoPeriodo: 0,
  contemplacoesPorMes: [],
  vendasPorMes: {},
};

/** Quantos CLIENTES cabem nas listas do dashboard. */
const TOP_CLIENTES = 5;

const buildMonthlyBuckets = () => {
  // 12 meses ROLANTES — a janela de contexto dos cards: o rótulo do mês
  // anterior no badge de produção, a linhazinha do Ticket médio e as
  // contemplações por mês. Os períodos civis (mês, semestre, ano) NÃO saem
  // daqui: saem de `vendasPorMes`.
  const buckets: { key: string; label: string; count: number; valor: number }[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''),
      count: 0,
      valor: 0,
    });
  }
  return buckets;
};

type Escopo = {
  scopeLoading: boolean;
  isSuperAdmin: boolean;
  currentCrmUserId: number | null;
  currentCpf: string | null;
  scopedCrmUserIds: number[];
  scopedCpfs: string[];
  /**
   * Vendedores escolhidos no topo. Lista VAZIA = não há recorte (super admin
   * vendo a casa). Virou lista quando o pedido passou a ser comparar mais de
   * um especialista, ou um gerente inteiro, sem trocar um por vez.
   */
  viewAsCrmUserIds: number[];
  cpfById: Map<number, string>;
};

export const useDadosInicio = ({
  scopeLoading,
  isSuperAdmin,
  currentCrmUserId,
  currentCpf,
  scopedCrmUserIds,
  scopedCpfs,
  viewAsCrmUserIds,
  cpfById,
}: Escopo) => {
  const [data, setData] = useState<DadosInicio>(vazio);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (scopeLoading) return;

    let active = true;
    const viewingAsVendedor = isSuperAdmin && viewAsCrmUserIds.length > 0;
    // sales_person_ids a filtrar. null = sem filtro (só super admin vendo "Todos").
    const effectiveIds: number[] | null = viewingAsVendedor
      ? viewAsCrmUserIds
      : isSuperAdmin
        ? null
        : scopedCrmUserIds;
    const effectiveCpfs: string[] | null = viewingAsVendedor
      ? viewAsCrmUserIds.map((id) => cpfById.get(id) ?? '').filter(Boolean)
      : isSuperAdmin
        ? null
        : scopedCpfs;
    // Usuário não-admin sem escopo => não mostra nada (evita vazar dados de outros).
    const noScope = !isSuperAdmin && scopedCrmUserIds.length === 0;

    const applyUserScope = <T,>(query: T): T => {
      if (effectiveIds && effectiveIds.length > 0) {
        // @ts-expect-error supabase builder chaining
        return query.in('sales_person_id', effectiveIds);
      }
      return query;
    };

    const load = async () => {
      try {
        setLoading(true);

        if (noScope) {
          if (active) setData(vazio);
          return;
        }

        // Vendas confirmadas (ativas) — base para KPIs, curvas e contemplações.
        // Lê da VIEW (não da tabela): `contemplado`/`data_contemplacao` são
        // overlay do snapshot de produção e só existem em
        // servopa_vendas_with_details.
        // Em páginas: a carteira confirmada (851 em 07/10, +~100/mês) ia passar
        // das 1000 linhas do PostgREST e os KPIs subcontariam.
        const vendasQuery = () =>
          supabase
            .from('servopa_vendas_with_details')
            .select('valor_credito, venda_data, status, sales_person_id, contemplado, data_contemplacao')
            .eq('status', 'confirmed');
        const { data: vendas } = await todasAsLinhas(() => applyUserScope(vendasQuery()));

        // Vendas pendentes (pagamento) — card "Aguardando pagamento".
        const pendingQuery = supabase
          .from('servopa_vendas_with_details')
          .select('id, nome_consorciado, contrato, group_number, valor_parcela, valor_credito, status, created_at')
          .in('status', PENDING_STATUSES)
          .order('created_at', { ascending: false });
        const { data: pendentes } = await applyUserScope(pendingQuery);

        // Propostas em aberto (geradas, ainda não fechadas e NÃO arquivadas).
        const propostasQuery = supabase
          .from('proposal_simulations')
          .select('id, sales_person_id', { count: 'exact' })
          .eq('status', 'generated')
          .is('archived_at', null);
        const { data: propostasRows, count: propostasCount } = await applyUserScope(propostasQuery);

        // Inadimplentes (escopo por CPF, igual à InadimplentesPage).
        const { data: inadAll } = await supabase
          .from('servopa_inadimplentes_current')
          // `parcelas_em_aberto` (migration 165) substitui `dias_atraso`, que
          // apesar do nome nunca guardou dias. Citar coluna inexistente faz o
          // PostgREST devolver 400, então esta linha exige a 165 aplicada no
          // banco alvo antes do deploy do front.
          .select('id, consorciado, vendedor_nome, parcelas_em_aberto, valor_atraso, vendedor_cpf_cnpj')
          .order('valor_atraso', { ascending: false });

        let inadScoped = (inadAll || []) as InadimplenteRow[];
        if (effectiveCpfs) {
          const cpfSet = new Set(effectiveCpfs);
          inadScoped = inadScoped.filter((row) => cpfSet.has(toDigits(row.vendedor_cpf_cnpj)));
        }

        // Comissões do mês — apenas super admin (fórmula ainda em validação).
        let comissoesMes = 0;
        if (isSuperAdmin) {
          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
            .toISOString()
            .slice(0, 10);
          // Pelo fechamento, não pela linha: o Incentivo não tem data de
          // pagamento por linha e sumia inteiro (R$ 4–25 mil/mês). O Total
          // Líquido é o que a Servopa efetivamente pagou (comissão + seguro −
          // estornos), já somado por ela.
          const { data: fechamentos } = await supabase
            .from('servopa_comissoes')
            .select('total_liquido, emissao_date')
            .gte('emissao_date', monthStart);
          comissoesMes = (fechamentos || []).reduce(
            (sum, c) => sum + Number(c.total_liquido || 0),
            0,
          );
        }

        // Agregações.
        type VendaRow = {
          valor_credito: number | null;
          venda_data: string | null;
          sales_person_id: number | null;
          contemplado: boolean | null;
          data_contemplacao: string | null;
        };
        const vendasList = (vendas || []) as VendaRow[];
        const vendasAtivasValor = vendasList.reduce(
          (sum, v) => sum + Number(v.valor_credito || 0),
          0,
        );

        const nowAgg = new Date();
        const anoPrefix = String(nowAgg.getFullYear());
        const mesPrefix = `${anoPrefix}-${String(nowAgg.getMonth() + 1).padStart(2, '0')}`;
        const mesAnterior = new Date(nowAgg.getFullYear(), nowAgg.getMonth() - 1, 1);
        const mesAnteriorPrefix = `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, '0')}`;
        const diaHoje = nowAgg.getDate();
        let vendasAnoValor = 0;
        let vendasAnoCount = 0;
        let vendasMesValor = 0;
        let vendasMesCount = 0;
        let contemplacoesMesCount = 0;
        let contemplacoesMesValor = 0;
        let vendasMesAnteriorMesmoPeriodo = 0;
        const porDia = new Array<number>(diaHoje + 1).fill(0);
        const vendasPorMes: VendasPorMes = {};
        vendasList.forEach((v) => {
          const valor = Number(v.valor_credito || 0);
          // Contemplações do mês têm régua própria: a DATA da contemplação,
          // não a da venda — uma cota vendida em março contemplada hoje é
          // notícia de hoje.
          if (String(v.data_contemplacao ?? '').slice(0, 7) === mesPrefix) {
            contemplacoesMesCount += 1;
            contemplacoesMesValor += valor;
          }
          // Exclui contempladas dos totais de venda: elas ficam na aba
          // "Contemplados" de Vendas, e o card leva pra aba que conta só o
          // que ela mostra.
          if (!v.venda_data || v.contemplado) return;
          const dataStr = String(v.venda_data);
          // O mês da venda, para qualquer período que o card peça depois.
          const chaveMes = dataStr.slice(0, 7);
          const doMes = (vendasPorMes[chaveMes] ??= { valor: 0, count: 0 });
          doMes.valor += valor;
          doMes.count += 1;
          if (dataStr.slice(0, 4) === anoPrefix) {
            vendasAnoValor += valor;
            vendasAnoCount += 1;
          }
          if (dataStr.slice(0, 7) === mesPrefix) {
            vendasMesValor += valor;
            vendasMesCount += 1;
            const dia = Number(dataStr.slice(8, 10));
            if (dia >= 1 && dia <= diaHoje) porDia[dia] += valor;
          }
          if (
            dataStr.slice(0, 7) === mesAnteriorPrefix &&
            Number(dataStr.slice(8, 10)) <= diaHoje
          ) {
            vendasMesAnteriorMesmoPeriodo += valor;
          }
        });

        // Curva acumulada do mês, do dia 1 até hoje — é o gráfico grande.
        const producaoDiaria: { dia: number; acumulado: number }[] = [];
        let acumulado = 0;
        for (let dia = 1; dia <= diaHoje; dia++) {
          acumulado += porDia[dia];
          producaoDiaria.push({ dia, acumulado });
        }

        const isOwnVenda = (sid: unknown) =>
          currentCrmUserId != null && Number(sid) === currentCrmUserId;
        const vendasAtivasOwnCount = vendasList.filter((v) => isOwnVenda(v.sales_person_id)).length;
        const propostasOwnCount = (propostasRows || []).filter((p) =>
          isOwnVenda((p as { sales_person_id?: number | null }).sales_person_id),
        ).length;
        const inadOwnCount = currentCpf
          ? inadScoped.filter((row) => toDigits(row.vendedor_cpf_cnpj) === currentCpf).length
          : 0;

        const buckets = buildMonthlyBuckets();
        const bucketByKey = new Map(buckets.map((b) => [b.key, b]));

        const contemplacoesPorMes = new Array<number>(buckets.length).fill(0);
        const indicePorKey = new Map(buckets.map((b, i) => [b.key, i]));
        vendasList.forEach((v) => {
          if (v.venda_data) {
            const bucket = bucketByKey.get(String(v.venda_data).slice(0, 7));
            if (bucket) {
              bucket.count += 1;
              bucket.valor += Number(v.valor_credito || 0);
            }
          }
          if (v.data_contemplacao) {
            const i = indicePorKey.get(String(v.data_contemplacao).slice(0, 7));
            if (i != null) contemplacoesPorMes[i] += 1;
          }
        });

        const pendingList = (pendentes || []) as PendingSale[];
        const pendingValor = pendingList.reduce(
          (sum, p) => sum + Number(p.valor_credito || 0),
          0,
        );

        if (active) {
          setData({
            vendasAtivasCount: vendasList.length,
            vendasAtivasValor,
            vendasAtivasOwnCount,
            vendasAnoValor,
            vendasAnoCount,
            vendasMesValor,
            vendasMesCount,
            inadCount: inadScoped.length,
            inadValor: inadScoped.reduce((sum, i) => sum + Number(i.valor_atraso || 0), 0),
            inadOwnCount,
            propostasCount: propostasCount || 0,
            propostasOwnCount,
            comissoesMes,
            pendingCount: pendingList.length,
            pendingValor,
            monthly: buckets.map(({ key, label, count, valor }) => ({
              chave: key,
              label,
              count,
              valor,
            })),
            topInadimplentes: agruparInadimplentes(inadScoped).slice(0, TOP_CLIENTES),
            topPendentes: agruparPendentes(pendingList).slice(0, TOP_CLIENTES),
            producaoDiaria,
            contemplacoesMesCount,
            contemplacoesMesValor,
            vendasMesAnteriorMesmoPeriodo,
            contemplacoesPorMes,
            vendasPorMes,
          });
        }
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        if (active) setData(vazio);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [scopeLoading, isSuperAdmin, currentCrmUserId, currentCpf, scopedCrmUserIds, scopedCpfs, // Conteúdo, não referência: o pai recria a lista a cada render.
    viewAsCrmUserIds.join(','), cpfById]);

  return { data, loading };
};
